import { Amplify } from 'aws-amplify';
import { Hub } from 'aws-amplify/utils';
import { signOut, signInWithRedirect, fetchAuthSession } from 'aws-amplify/auth';
import { encryptData, decryptData } from '../utils/secureStorage';

export type IAuthUser = {

    first_name: string;
    last_name: string;
    display_name: string;
    email: string;
    employee_id: string;
    roles: string;
    otherRoles?: string;
}


export type IAuthUserDetails = {

    first_name: string;
    last_name: string;
    display_name: string;
    email: string;
    employee_id: string;
    roles: string;
    otherRoles?: string;
}


const CLIENT_ID = process.env.REACT_APP_AWS_COGNITO_CLIENT_ID || '';
const POOL_ID = process.env.REACT_APP_AWS_COGNITO_POOL_ID || '';
const REGION = process.env.REACT_APP_AWS_COGNITO_REGION || 'us-west-2';
const DOMAIN_URL_REDIRECT = process.env.REACT_APP_AWS_COGNITO_REDIRECT_URL || window.location.origin;
const OAUTH_DOMAIN_URL = process.env.REACT_APP_COGNITO_DOMAIN || '';

const scopes = [
    'email',
    'openid',
    'profile',
    'aws.cognito.signin.user.admin'
]

const requiredEnv = [
    'REACT_APP_AWS_COGNITO_REGION',
    'REACT_APP_AWS_COGNITO_POOL_ID',
    'REACT_APP_AWS_COGNITO_CLIENT_ID',
    'REACT_APP_COGNITO_DOMAIN',
    'REACT_APP_AWS_COGNITO_TOKEN_SCOPE',
    'REACT_APP_AWS_COGNITO_REDIRECT_URL',
];

// Validate required environment variables
// for (const variable of requiredEnv) {
//     const value = process.env[variable] || '';
//     if (!value || value.includes('#' + '%' + '{')) {
//         // eslint-disable-next-line no-alert
//         alert(
//             `Environment Variable Error: Please configure ${variable} properly in .env or .env.local file`
//         );
//     }
// }



const config = {
    Auth: {
        Cognito: {
            userPoolId: POOL_ID,
            userPoolClientId: CLIENT_ID,
            loginWith: {
                oauth: {
                    domain: OAUTH_DOMAIN_URL,
                    scopes: scopes,
                    redirectSignIn: [DOMAIN_URL_REDIRECT],
                    redirectSignOut: [DOMAIN_URL_REDIRECT],
                    responseType: 'code' as const,
                },
            },
        },
    }
}

/* console.log('Amplify OAuth Config:', {
    domain: OAUTH_DOMAIN_URL,
    redirectSignIn: DOMAIN_URL_REDIRECT,
    clientId: CLIENT_ID,
    userPoolId: POOL_ID,
    region: REGION
}); */

Amplify.configure(config)

const UPDATE_TOKENS_INTERVAL = 60000 * 15;
let refreshIntervalId: NodeJS.Timeout | null = null;
let refreshAttempts = 0;
const MAX_REFRESH_ATTEMPTS = 3;
let lastRefreshTime = 0;

export const setUser = (user: IAuthUser) => {
    // lgtm [js/clear-text-storage-of-sensitive-data]
    const encryptedData = encryptData(JSON.stringify(user));
    localStorage.setItem(`${CLIENT_ID}_user_data`, encryptedData);
};


export const getUser = () => {

    const encryptedUser = localStorage.getItem(`${CLIENT_ID}_user_data`);
    if (!encryptedUser) return null;
    const decryptedUser = decryptData(encryptedUser);
    return decryptedUser ? (JSON.parse(decryptedUser) as IAuthUser) : null;
};

export const getUserFullName = () => {
    const user = getUser();
    if (user) {
        const userName = `${user.last_name}, ${user.first_name}`;
        return userName;
    }
    return 'NA';
};

export const getUserEmail = () => {
    const user = getUser();
    if (user) {
        const userEmail = user.display_name;
        return userEmail;
    }
    return 'NA';
}




export const getUserInitials = () => {
    const user = getUser();
    if (user) {
        const userName = [user.first_name, user.last_name];
        return userName.reduce((prev, curr) => {
            return `${prev}${curr.substring(0, 1)}`;
        }, '');
    }
    return 'NA';
};

export const getUserRole = () => {
    const user = getUser();
    if (user) {
        return user.otherRoles || 'User';
    }
    return 'User';
};

export const getSingleUser = (payload: any): IAuthUserDetails => {
    if (payload) {
        return {
            first_name: payload.first_name,
            last_name: payload.last_name,
            display_name: payload.display_name,
            email: payload.email,
            employee_id: payload.employee_id,
            roles: payload.nickname,
        };
    }
    return {} as IAuthUserDetails;
};


export const getToken = () => {
    return localStorage.getItem(`${CLIENT_ID}_user_token`);
};


export const setToken = (token: string) => {
    localStorage.setItem(`${CLIENT_ID}_user_token`, token);
};


export const clearStorage = () => {
    localStorage.removeItem(`${CLIENT_ID}_user_data`);
    localStorage.removeItem(`${CLIENT_ID}_user_token`);
    sessionStorage.clear();
};


export const signIn = async () => {
    const currentPageUrl = encodeURI(window.location.href);
    // const isCognitoCallback = window.location.search.includes('code=');
    if (DOMAIN_URL_REDIRECT !== currentPageUrl) {
        sessionStorage.setItem('currentPage', currentPageUrl);
    }

    //console.log('Starting OAuth flow with redirect URL:', DOMAIN_URL_REDIRECT);

    // Prevent infinite redirect loop: do not redirect if already on Cognito callback (?code= in URL)
    // if (!isCognitoCallback) {
    try {
        const identity_provider = process.env.REACT_APP_IDP_NAME ?? '';
        //console.log('Using identity provider:', identity_provider);
        await signInWithRedirect({ provider: { custom: identity_provider } });
    } catch (error) {
        //console.error('signInWithRedirect failed, using manual redirect:', error);
        // Fallback to manual redirect
        const identity_provider = process.env.REACT_APP_IDP_NAME ?? '';
        const idpParam = identity_provider ? `&identity_provider=${identity_provider}` : '';
        window.location.href = `https://${OAUTH_DOMAIN_URL}/oauth2/authorize?redirect_uri=${encodeURIComponent(DOMAIN_URL_REDIRECT)}&response_type=code&client_id=${CLIENT_ID}&scope=${encodeURIComponent(
            scopes.join(' ')
        )}${idpParam}`;
    }
    // }
};


export const logout = async () => {
    try {
        stopTokenRefresh();
        clearStorage();
        localStorage.removeItem(`${CLIENT_ID}_url`);
        localStorage.removeItem(`${CLIENT_ID}_attempt`);
        // clearAllCookies();

        await signOut();
        // window.location.href = `https://login.microsoftonline.com/common/oauth2/v2.0/logout?post_logout_redirect_uri=${DOMAIN_URL_REDIRECT}/logout`;
    } catch (error) {
        // Silent error handling
    }
};



export const redirect = () => {
    const url = localStorage.getItem(`${CLIENT_ID}_url`);
    const attempt = localStorage.getItem(`${CLIENT_ID}_attempt`);

    if (
        (url && url.includes('unauthorized')) ||
        (attempt && Number(attempt) > 3)
    ) {
        localStorage.removeItem(`${CLIENT_ID}_url`);
        localStorage.removeItem(`${CLIENT_ID}_attempt`);
        window.location.href = `${window.location.origin}/logout`;
        return;
    }

    localStorage.setItem(
        `${CLIENT_ID}_url`,
        window.location.href.replace(/logout.*/, '')
    );

    clearStorage();

    window.location.href = `https://${config.Auth.Cognito.loginWith.oauth.domain}/oauth2/authorize?redirect_uri=${encodeURIComponent(
        config.Auth.Cognito.loginWith.oauth.redirectSignIn[0]
    )}&response_type=code&client_id=${encodeURIComponent(
        config.Auth.Cognito.userPoolClientId
    )}&scope=${encodeURIComponent(config.Auth.Cognito.loginWith.oauth.scopes.join(' '))}`;
};

export const refreshToken = async (isManual = false) => {
    try {
        const session = await fetchAuthSession({ forceRefresh: true });

        if (session && session.tokens && session.tokens.accessToken) {
            const newToken = session.tokens.accessToken.toString();
            setToken(newToken);
            refreshAttempts = 0;
            lastRefreshTime = Date.now();
            return { success: true, error: null };
        }
        return { success: false, error: 'No valid tokens found' };
    } catch (err: any) {
        refreshAttempts++;

        // Only redirect on manual refresh or after max attempts
        if (isManual || refreshAttempts >= MAX_REFRESH_ATTEMPTS) {
            refreshAttempts = 0; // Reset for next time
            redirect();
            return { success: false, error: err.message || 'Token refresh failed' };
        }

        return { success: false, error: err.message || 'Token refresh failed' };
    }
};

export const startTokenRefresh = () => {
    stopTokenRefresh();
    if (!hasAuth()) {
        return;
    }
    // Initial refresh if it's been more than 15 minutes since last refresh
    const timeSinceLastRefresh = Date.now() - lastRefreshTime;
    if (timeSinceLastRefresh > UPDATE_TOKENS_INTERVAL) {
        refreshToken();
    }

    // Set up periodic refresh
    refreshIntervalId = setInterval(() => {
        (async () => {
            if (hasAuth()) {
                await refreshToken();
            } else {
                stopTokenRefresh();
            }
        })().catch(() => {
            // Silent error handling for async interval
        });
    }, UPDATE_TOKENS_INTERVAL);
};

export const stopTokenRefresh = () => {
    if (refreshIntervalId) {
        clearInterval(refreshIntervalId);
        refreshIntervalId = null;
    }
};

// Track authentication check state to prevent infinite loops
let isCheckingAuth = false;
let lastAuthCheckTime = 0;
const AUTH_CHECK_COOLDOWN = 10000; // 10 seconds cooldown between auth checks

export const hasAuth = () => {
    const user = getUser();
    const token = getToken();

    if (!user) {
        return false;
    }
    if (!token) {
        return false;
    }

    return true;
};

// Enhanced authentication check with token validation
export const validateAuth = async () => {
    // Basic auth check first
    if (!hasAuth()) {
        return false;
    }

    // Prevent infinite loops and excessive checks
    const now = Date.now();
    if (isCheckingAuth || now - lastAuthCheckTime < AUTH_CHECK_COOLDOWN) {
        return true; // Assume auth is valid during cooldown to prevent loops
    }

    const token = getToken();
    if (!token) {
        clearStorage();
        redirect();
        return false;
    }

    // Check if token might be expired (basic JWT expiration check)
    try {
        const tokenParts = token.split('.');
        if (tokenParts.length === 3) {
            const payload = JSON.parse(atob(tokenParts[1]));
            const expiration = payload.exp * 1000; // Convert to milliseconds
            const currentTime = Date.now();

            // If token expires within the next 5 minutes, try to refresh
            if (expiration && currentTime + 5 * 60 * 1000 >= expiration) {
                isCheckingAuth = true;
                lastAuthCheckTime = now;

                try {
                    const refreshResult = await refreshToken(true);

                    if (refreshResult.success) {
                        isCheckingAuth = false;
                        return true;
                    }
                    isCheckingAuth = false;
                    // Clear storage and redirect
                    clearStorage();
                    redirect();
                    return false;
                } catch (error) {
                    isCheckingAuth = false;
                    // Clear storage and redirect
                    clearStorage();
                    redirect();
                    return false;
                }
            }
        }
    } catch (error) {
        // If we can't parse the token, it might be invalid
        clearStorage();
        redirect();
        return false;
    }

    // If we get here, token should be valid
    return true;
};

Hub.listen('auth', (data) => {
    //console.log(data)
    const { event } = data.payload;

    switch (event) {
        case 'signInWithRedirect':
        case 'signedIn': {
            fetchAuthSession()
                .then((session) => {
                    if (session && session.tokens && session.tokens.idToken) {
                        const { payload } = session.tokens.idToken;

                        const role_name = String(
                            payload['custom:role_name'] ||
                            payload.role_name ||
                            payload.nickname ||
                            'User'
                        );
                        const other_roles = String(
                            payload['custom:other_roles'] || payload.other_roles || ''
                        );

                        let formattedRoles = '';
                        if (role_name || other_roles) {
                            const rolesArray = [
                                role_name,
                                ...(other_roles ? other_roles.split(',') : []),
                            ].filter(Boolean);
                            formattedRoles = `[${rolesArray.join(', ')}]`;
                        } else {
                            formattedRoles = '[User]';
                        }

                        setUser({
                            first_name: String(payload.given_name ?? ''),
                            last_name: String(payload.family_name ?? ''),
                            display_name: String(
                                payload.name ??
                                `${String(payload.given_name ?? '')} ${String(payload.family_name ?? '')}`
                            ),
                            email: String(payload.email ?? ''),
                            employee_id: String(payload.profile ?? payload.employee_id ?? payload.sub ?? ''),
                            roles: role_name,
                            otherRoles: formattedRoles,
                        });

                        // Set access token
                        if (session.tokens.accessToken) {
                            setToken(session.tokens.accessToken.toString());
                            lastRefreshTime = Date.now(); // Mark token as fresh
                        }

                        // Start automatic token refresh for authenticated users
                        startTokenRefresh();

                        // Redirect logic
                        window.location.href =
                            localStorage.getItem(`${CLIENT_ID}_url`) || window.location.href;
                    }
                    return; // Satisfy promise/always-return
                })
                .catch((error) => {
                    // Silent error handling
                    return; // Satisfy promise/always-return
                });
            break;
        }
        case 'tokenRefresh': {
            lastRefreshTime = Date.now();
            // Fetch and persist the refreshed token
            fetchAuthSession()
                .then((session) => {
                    if (session && session.tokens && session.tokens.accessToken) {
                        setToken(session.tokens.accessToken.toString());
                        //console.log('Token refreshed and persisted to localStorage');
                    }
                    return;
                })
                .catch((error) => {
                    //console.warn('Failed to persist refreshed token:', error);
                    return;
                });
            break;
        }
        case 'signedOut': {
            stopTokenRefresh(); // Stop token refresh on sign out
            clearStorage();
            break;
        }
        case 'tokenRefresh_failure': {
            // Don't immediately redirect - let the automatic refresh mechanism handle retries
            break;
        }
    }
});
